'use client';
// Revue périodique des intervalles de référence — ISO 15189:2022 §7.3.3.
//
// Le référentiel du produit déclarait lui-même l'écart : « les valeurs de
// référence figurent sur la fiche d'examen ; MANQUE la revue périodique tracée
// de ces intervalles ». Le champ existait, l'acte de revue n'existait pas.
//
// ── Quatre choses que cet écran refuse de faire ─────────────────────────────
//
//  · INTERPRÉTER l'intervalle. « Na 136–145 ; K 3,5–5,1 ; Cl 98–107 mmol/L » est
//    du texte libre : il est affiché tel quel, jamais découpé en bornes, jamais
//    comparé. Un extracteur produirait des valeurs fausses en silence sur une
//    partie du catalogue — dans un document que le laboratoire publie à ses
//    préleveurs.
//
//  · ÉCRIRE DANS LA FICHE. Conclure « il faut changer » ouvre la fiche dans le
//    manuel, où vit le circuit de proposition et de diffusion versionnée. Un
//    second chemin d'écriture du catalogue finirait par diverger du premier.
//
//  · TRAITER « INCHANGÉ » COMME UN NON-ÉVÉNEMENT. C'est la conclusion la plus
//    fréquente et la seule que l'auditeur vient chercher — « montrez-moi que
//    vous avez regardé ». Elle est proposée d'emblée, elle produit la même
//    trace signée, elle repousse l'échéance de la même façon.
//
//  · CONFONDRE « pas encore paramétré » avec « tout est à jour ». Tant que le
//    RQ n'a pas fixé la périodicité et la date d'origine, AUCUNE échéance
//    n'existe, et l'écran le dit en tête — plutôt que d'afficher zéro retard.
//
// Et une chose qu'il assume : la revue se conduit EN LOT. Le catalogue compte
// 157 fiches ; une notice de fournisseur couvre le panel entier d'un automate.
// On sélectionne, on conclut une fois — et le moteur écrit une trace par fiche,
// avec la photocopie de SON intervalle et son propre événement au journal.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { fmtDate } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, FilterBar, RefISO,
  FieldError, RequiredHint, useBusy, usePagination, Pagination, LienExterne } from '@/components/ui';
import { H1 } from './ressources/commun';
import { RegistresLies } from './manuel/RegistresLies';
import { trancheAgeLisible } from '@/shared/ageLisible';
import { OngletPediatrie } from './manuel/Pediatrie';
import { OngletSources } from './manuel/Sources';
import type { InstantaneIntervalle } from '@/lib/types';
import { Norme } from '@/components/Norme';
import type { GedRevueRefDossier, GedRevueRefLigne, GedRevueRefTrace } from '@/lib/types';
import { useFormulaire } from '@/components/Formulaire';
import { useUrlsModules } from '@/shared/urlsModulesClient';


import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
};
const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5,
  color: 'var(--text-body)', lineHeight: 1.5,
};

/** L'intervalle publié, affiché VERBATIM et en chasse fixe.
 *
 *  La chasse fixe n'est pas une coquetterie : elle rend lisibles les tirets
 *  demi-cadratins, les virgules décimales et les points-virgules qui séparent
 *  les analytes — c'est-à-dire exactement ce qu'on relit pendant une revue. */
/**
 * LES INTERVALLES D'UN PARAMÈTRE, ligne à ligne.
 *
 * ⚠️ Remplace l'affichage du texte libre `valeursRef` de l'examen (24/08/2026).
 * Une phrase qui empilait « Hb 12–16 ; Leuco 4–10 ; Plaq 150–400 » ne se relit
 * pas : on conclut « inchangé » sur une chaîne qu'on n'a pas vraiment lue.
 * Ligne à ligne, chaque strate se voit — et c'est bien elle qu'on déclare
 * inchangée.
 */
function Intervalles({ lignes }: { lignes: InstantaneIntervalle[] }) {
  if (!lignes.length) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text-faint)', fontStyle: 'italic' }}>
        aucun intervalle adopté
      </div>
    );
  }
  const borne = (i: InstantaneIntervalle) => {
    if (i.texteLibre) return i.texteLibre;
    const u = i.unite ? ` ${i.unite}` : '';
    const n = (v: number) => String(v).replace('.', ',');
    if (i.borneMin !== null && i.borneMax !== null) return `${n(i.borneMin)} – ${n(i.borneMax)}${u}`;
    if (i.borneMax !== null) return `< ${n(i.borneMax)}${u}`;
    if (i.borneMin !== null) return `> ${n(i.borneMin)}${u}`;
    return '—';
  };
  const strate = (i: InstantaneIntervalle) => [
    i.nature === 'zone-therapeutique' ? 'Zone thérapeutique'
      : i.nature === 'seuil-decision' ? 'Seuil de décision' : '',
    i.sexe === 'masculin' ? 'Homme' : i.sexe === 'feminin' ? 'Femme' : '',
    trancheAgeLisible(i.ageMin, i.ageMax),
    i.grossesse && i.grossesse !== 'indifferent' ? i.grossesse : '',
    i.condition || '',
  ].filter(Boolean).join(', ');
  return (
    <div style={{
      fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-body)',
      background: 'var(--gray-25)', border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-sm)', padding: '6px 9px',
      display: 'grid', gap: 3,
    }}>
      {lignes.map((i, n) => (
        <div key={n} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {strate(i) && <span style={{ color: 'var(--text-muted)' }}>{strate(i)}</span>}
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{borne(i)}</span>
          {i.source && <span style={{ color: 'var(--text-faint)' }}>· {i.source}</span>}
        </div>
      ))}
    </div>
  );
}

/** État d'échéance. Quatre états, et « non paramétré » n'est pas « à jour ». */
function Echeance({ l, sansParametrage }: { l: GedRevueRefLigne; sansParametrage: boolean }) {
  if (l.sansIntervalle) {
    return (
      <span title="Cette fiche ne publie aucun intervalle de référence : il n’y a rien à revoir. Elle n’est ni à jour, ni en retard."
        style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic', cursor: 'help' }}>
        aucun intervalle publié
      </span>
    );
  }
  if (sansParametrage || !l.echeance) {
    return (
      <span title="La périodicité de revue n’a pas été fixée : aucune échéance ne peut être calculée. Ce n’est pas « à jour »."
        style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 700, cursor: 'help' }}>
        échéance non calculée
      </span>
    );
  }
  if (l.enRetard) {
    return (
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>
        échue le {fmtDate(l.echeance)}
      </span>
    );
  }
  return (
    <span style={{ fontSize: 12.5, fontWeight: l.bientot ? 700 : 400, color: l.bientot ? 'var(--amber-600)' : 'var(--text-muted)' }}>
      à revoir le {fmtDate(l.echeance)}
    </span>
  );
}

/** Historique de revue d'une fiche : la suite des constats, le plus récent en
 *  tête. Chaque ligne porte QUI a regardé, QUAND, SUR QUOI il s'est fondé, et
 *  ce qu'il a lu ce jour-là — c'est ce qui en fait une trace opposable. */
function Historique({ lignes, sources }: { lignes: GedRevueRefTrace[]; sources: { cle: string; label: string }[] }) {
  const label = (cle: string) => sources.find((s) => s.cle === cle)?.label || cle || '—';
  return (
    <div style={{ marginTop: 8, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      {[...lignes].reverse().map((r, i) => (
        <div key={r.documentId} style={{ padding: '8px 11px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', display: 'grid', gap: 4 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{fmtDate(r.dateRevue)}</span>
            <span style={{
              fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px',
              background: r.conclusion === 'inchange' ? 'var(--green-50)' : 'var(--cyan-50)',
              color: r.conclusion === 'inchange' ? 'var(--green-600)' : 'var(--cyan-700)',
            }}>
              {r.conclusion === 'inchange' ? 'inchangé' : 'à modifier'}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>revu par {r.revueParNom || '—'}</span>
          </div>
          {r.commentaire && <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{r.commentaire}</div>}
          <div>
            {/* La SOURCE ne se recopie plus dans la revue : chaque intervalle
                porte la sienne, et l'instantané la garde telle quelle. Deux
                copies d'une même donnée finissent par diverger, et la trace
                donnerait alors tort à la donnée. */}
            <span style={{ ...lbl, marginBottom: 2 }}>Ce qui a été lu ce jour-là</span>
            <Intervalles lignes={r.instantane || []} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Le paramétrage : périodicité par défaut, exceptions PAR FAMILLE, origine.
 *
 *  Par famille et non par fiche — 157 saisies à tenir, contre une douzaine.
 *  L'échéance, elle, reste par fiche : elle se calcule à partir de la dernière
 *  revue de CETTE fiche. On garde la traçabilité fiche par fiche que l'audit
 *  exige, sans la saisie fiche par fiche que personne ne tiendrait. */
function Parametrage({ d, onFait, onAnnule }: {
  d: GedRevueRefDossier; onFait: () => void; onAnnule: () => void;
}) {
  const p = d.parametrage;
  const [defaut, setDefaut] = useState(String(p?.periodiciteDefautMois ?? 36));
  const [origine, setOrigine] = useState(p?.dateOrigine || '');
  const [rappel, setRappel] = useState(String(p?.rappelAvantJours ?? 30));
  const [parFamille, setParFamille] = useState<Record<string, string>>(() => {
    const src = p?.parFamille || {};
    return Object.fromEntries(d.familles.map((f) => [f, src[f] === undefined ? '' : String(src[f])]));
  });
  /* L'empreinte du réglage LU à l'ouverture (14/10/2026), figée : un réglage changé par un autre
     entre-temps fait refuser l'enregistrement (409) au lieu d'être écrasé. Le type partagé
     (`lib/types.ts`) ne la porte pas encore : lue ici sans y toucher. */
  const [versionLue] = useState(() => (d as GedRevueRefDossier & { versionParametrage?: string }).versionParametrage);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  /* Ces deux champs portaient l'étoile du champ obligatoire et n'étaient
     contrôlés NULLE PART côté écran : le serveur refusait, et le refus
     revenait sous forme de message générique en bas de formulaire. */
  const declaration = React.useMemo(() => [
    { id: 'rr-def', label: 'Périodicité par défaut, en mois', valeur: defaut, requis: true },
    { id: 'rr-org', label: 'Date d’origine', valeur: origine, requis: true },
  ], [defaut, origine]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const enregistrer = () => run('param', async () => {
    if (!verifier()) return;
    setErreur('');
    const r = await fetchEcriture('/api/ged/ged/revues-ref/parametrage', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        periodiciteDefautMois: Number(defaut),
        dateOrigine: origine,
        rappelAvantJours: Number(rappel),
        parFamille,
        version: versionLue,
      }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  return (
    <div ref={refFormulaire} style={{ ...card, padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Rythme de revue des intervalles
        </h2>
        <RequiredHint />
      </div>
      <Bandeau />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        La périodicité se règle <strong>par famille d’analyses</strong>, pas par fiche : sur
        {' '}{d.compte.parametres} fiches, une valeur par fiche serait {d.compte.parametres} saisies à tenir à jour
        pour une information qui ne varie pas à l’intérieur d’un même secteur. L’<strong>échéance</strong>,
        elle, reste calculée fiche par fiche — dernière revue de cette fiche, plus la périodicité de sa famille.
      </p>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        <div>
          <label style={lbl} htmlFor="rr-def">Périodicité par défaut, en mois <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="rr-def" type="number" min={1} max={120} value={defaut}
            onChange={(e) => setDefaut(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={lbl} htmlFor="rr-org">Date d’origine <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="rr-org" type="date" value={origine} onChange={(e) => setOrigine(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={lbl} htmlFor="rr-rap">Préavis d’échéance, en jours</label>
          <input id="rr-rap" type="number" min={0} max={365} value={rappel}
            onChange={(e) => setRappel(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '84ch' }}>
        La <strong>date d’origine</strong> est celle à laquelle le laboratoire déclare ses intervalles établis.
        C’est depuis elle que court la première échéance des fiches jamais revues — sans elle, les
        {' '}{d.compte.aRevoir} fiches concernées seraient toutes en retard dès aujourd’hui, et le centre de
        tâches deviendrait illisible le jour même où on lui demande d’être cru.
      </p>

      <div>
        <span style={lbl}>Familles réglées à part</span>
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
          {d.familles.map((f) => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-body)' }}>{f}</span>
              <input type="number" min={1} max={120} value={parFamille[f] ?? ''}
                onChange={(e) => setParFamille((x) => ({ ...x, [f]: e.target.value }))}
                placeholder={defaut} aria-label={`Périodicité de la famille ${f}, en mois`}
                style={{ ...inputStyle, width: 84, height: 30, fontSize: 12.5 }} />
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>mois</span>
            </div>
          ))}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
          Laisser vide = la famille suit la périodicité par défaut.
        </p>
      </div>

      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer le rythme', libelleChargement: 'Enregistrement…', chargement: estOccupe('param'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
      <FieldError>{erreur}</FieldError>
    </div>
  );
}

/** Conduire la revue sur la sélection.
 *
 *  « Inchangé » est pré-coché : c'est la conclusion la plus fréquente, et la
 *  seule que l'auditeur vient vérifier. Elle n'est pas un raccourci — elle
 *  produit la même trace signée qu'une modification. */
function Conduire({ selection, sources, onFait, onAnnule }: {
  selection: GedRevueRefLigne[];
  sources: { cle: string; label: string }[];
  onFait: (renvoi: { code: string; libelle: string }[]) => void;
  onAnnule: () => void;
}) {
  const [conclusion, setConclusion] = useState<'inchange' | 'a_modifier'>('inchange');
  const [source, setSource] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  /* ⚠️ NI SOURCE NI CITATION NI POPULATION ne sont demandées depuis le
     24/08/2026. Elles l'étaient, et c'était une RECOPIE : chaque intervalle
     porte déjà sa source, saisie au moment de son adoption, et sa strate dit
     déjà sa population. Redemander la même chose à la revue, c'était deux
     copies d'une donnée — et deux copies finissent par diverger, la trace
     donnant alors tort à la donnée dont elle est issue. */
  const declarationRevue = React.useMemo(() => [
    { id: 'rr-concl', label: 'Conclusion', valeur: conclusion, requis: true },
  ], [conclusion]);
  const { ref: refRevue, verifier: verifierRevue, Bandeau: BandeauRevue } = useFormulaire(declarationRevue);

  const enregistrer = () => run('revue', async () => {
    if (!verifierRevue()) return;
    setErreur('');
    const r = await fetchEcriture('/api/ged/ged/revues-ref', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        parametres: selection.map((l) => l.code),
        conclusion, commentaire,
      }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    const j = await r.json().catch(() => ({}));
    onFait(j?.renvoi || []);
  });

  return (
    <div ref={refRevue} style={{ ...card, padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Revoir {selection.length} examen{selection.length > 1 ? 's' : ''}
        </h2>
        <RequiredHint />
      </div>
      <BandeauRevue />

      <div style={{ maxHeight: 190, overflow: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
        {selection.map((l, i) => (
          <div key={l.code} style={{ padding: '7px 11px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.code}</span>
              <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{l.libelle}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{l.famille}</span>
            </div>
            {/* L'intervalle est SOUS LES YEUX pendant qu'on conclut : conclure
                « inchangé » sans relire ce qu'on déclare inchangé n'est pas une
                revue, c'est une signature. */}
            <Intervalles lignes={l.intervalles} />
          </div>
        ))}
      </div>

      <div>
        <span style={lbl}>Conclusion <span style={{ color: 'var(--red-600)' }}>*</span></span>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--text-body)' }}>
            <input id="rr-concl" type="radio" name="rr-concl" checked={conclusion === 'inchange'}
              onChange={() => setConclusion('inchange')} />
            Intervalles inchangés
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--text-body)' }}>
            <input type="radio" name="rr-concl" checked={conclusion === 'a_modifier'}
              onChange={() => setConclusion('a_modifier')} />
            À modifier
          </label>
        </div>
        <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '84ch' }}>
          {conclusion === 'inchange'
            ? 'Une revue sans changement s’enregistre exactement comme une autre : c’est elle que l’auditeur vient chercher — « montrez-moi que vous avez regardé ».'
            : 'Cette conclusion ne modifie AUCUNE fiche. Elle sera tracée, puis vous serez renvoyé vers le circuit de proposition du manuel, seul chemin d’écriture du catalogue.'}
        </p>
      </div>

      <div>
        <label style={lbl} htmlFor="rr-com">
          Commentaire{conclusion === 'a_modifier' && <span style={{ color: 'var(--red-600)' }}> *</span>}
        </label>
        {/* ⚠️ PLUS DE SAISIE D'UN « NOUVEL INTERVALLE » ICI (24/08/2026).
            Ce champ écrivait un brouillon de fiche portant `valeursRef` — le
            texte libre de l'examen, c'est-à-dire précisément ce que la RQ a
            demandé de faire disparaître. Corriger un intervalle se fait à son
            registre, où chaque ligne est datée, adoptée et close nommément ;
            le faire ici l'aurait fait passer par le circuit éditorial du
            catalogue, qui n'est pas le sien. La revue CONSTATE, elle ne
            réécrit pas. */}
        {conclusion === 'a_modifier' && (
          <div style={{
            marginBottom: 10, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)',
            background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '8px 10px',
          }}>
            Dites ci-dessous ce qui doit changer. La correction elle-même se fait au
            <strong> référentiel des paramètres</strong> — en clôturant l’intervalle qui ne vaut plus
            et en adoptant celui qui le remplace. Les deux gestes se rangent au journal du
            paramètre, à côté de cette revue.
          </div>
        )}
        <textarea id="rr-com" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3}
          placeholder={conclusion === 'a_modifier'
            ? 'Ce qui doit changer, et pourquoi — ce texte sera repris dans la proposition.'
            : 'Ce que la revue a examiné et qui ne se lit pas dans la source…'}
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>

      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer la revue', libelleChargement: 'Enregistrement…', chargement: estOccupe('revue'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
      <FieldError>{erreur}</FieldError>
    </div>
  );
}

export function RevueRef() {
  // Adresse du catalogue résolue à l'exécution (posée par le layout).
  const { manuel: MANUEL_URL } = useUrlsModules();
  const [d, setD] = useState<GedRevueRefDossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [famille, setFamille] = useState('');
  const [coches, setCoches] = useState<Record<string, boolean>>({});
  const [conduit, setConduit] = useState(false);
  const [regle, setRegle] = useState(false);
  const [ouvert, setOuvert] = useState<Record<string, boolean>>({});
  const [renvoi, setRenvoi] = useState<{ code: string; libelle: string }[]>([]);
  /* A7 — la revue des tranches PÉDIATRIQUES est un second onglet, pas un écran
     à part : c'est la même matière, revue sous un autre angle. La ranger
     ailleurs aurait fait deux endroits où chercher « les intervalles ». */
  const [onglet, setOnglet] = useState<'periodique' | 'pediatrie' | 'sources'>('periodique');

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/revues-ref', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('lecture refusée'))))
      .then((j) => { if (vivant) { setD(j); setErreur(false); } })
      // On ne se tait pas : un écran vide parce que la lecture a échoué se lit
      // « aucune revue à faire », ce qui est le pire des messages sur celui-ci.
      .catch(() => { if (vivant) { setD(null); setErreur(true); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => { setConduit(false); setRegle(false); setCoches({}); setTour((t) => t + 1); };

  const filtrees = useMemo(() => {
    const base = (d?.lignes || []).filter((l) => {
      if (famille && l.famille !== famille) return false;
      if (filtre === 'retard') return l.enRetard;
      if (filtre === 'bientot') return l.bientot;
      if (filtre === 'jamais') return !l.sansIntervalle && l.jamaisRevue;
      if (filtre === 'amodifier') return l.derniere?.conclusion === 'a_modifier';
      if (filtre === 'sansintervalle') return l.sansIntervalle;
      return true;
    });
    const qq = q.trim().toLowerCase();
    return qq ? base.filter((l) => `${l.code} ${l.libelle} ${l.famille}`.toLowerCase().includes(qq)) : base;
  }, [d, filtre, q, famille]);
  const pages = usePagination(filtrees);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement des intervalles de référence…</p>;
  if (erreur || !d) {
    return (
      <p style={{ fontSize: 13, color: 'var(--red-600)' }}>
        Liste indisponible — le serveur n’a pas répondu. Ce n’est pas « aucune revue à faire » : réessayez.
      </p>
    );
  }

  const { compte, sources } = d;
  // Une fiche sans intervalle publié n'est jamais sélectionnable : il n'y a
  // rien à revoir, et le moteur refuserait de toute façon.
  const selection = filtrees.filter((l) => coches[l.code] && !l.sansIntervalle);
  const selectionnables = filtrees.filter((l) => !l.sansIntervalle);
  const toutCoche = selectionnables.length > 0 && selection.length === selectionnables.length;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Processus analytique · <RefISO code="7.3.5" /><Norme art="7.3.5" /></>}
        title="Intervalles de référence" />
      <RegistresLies actif="revueref" />

      <div style={{ display: 'flex', gap: 6 }}>
        <Segment ariaLabel="Section" valeur={onglet} onChange={setOnglet} options={([['periodique', 'Revue périodique'], ['pediatrie', 'Tranches pédiatriques'], ['sources', 'D’où viennent nos intervalles']] as const).map(([v, l]) => ({ valeur: v, libelle: l }))} />
      </div>

      {onglet === 'pediatrie' && <OngletPediatrie />}

      {onglet === 'sources' && <OngletSources />}

      {onglet === 'periodique' && (
      <>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        La norme n’exige pas que les intervalles changent : elle exige qu’ils soient
        {' '}<strong>revus périodiquement</strong>, et que le laboratoire puisse le montrer. Une revue qui
        conclut « inchangé » est donc un enregistrement à part entière — c’est même le plus fréquent, et
        celui que l’auditeur vient chercher. Cet écran <strong>ne modifie aucune fiche</strong> : une
        conclusion « à modifier » renvoie au circuit de proposition du manuel, seul chemin d’écriture du
        catalogue.
      </p>

      {d.sansParametrage && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>Le rythme de revue n’a pas encore été fixé.</strong>{' '}
          Aucune échéance n’est calculée et aucune fiche n’est déclarée en retard — ce qui ne veut
          <em> pas</em> dire que tout est à jour. Fixer la périodicité relève du responsable qualité :
          le logiciel ne décide pas à quel rythme un laboratoire revoit ce qu’il publie.
          {d.peutParametrer && !regle && (
            <>
              {' '}
              <Bouton variante="primaire" taille="sm" onClick={() => setRegle(true)} style={{ marginTop: 6 }}>
                Fixer le rythme de revue
              </Bouton>
            </>
          )}
        </div>
      )}

      {renvoi.length > 0 && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>
            Revue enregistrée — {renvoi.length} intervalle{renvoi.length > 1 ? 's' : ''} à modifier.
          </strong>{' '}
          La trace est prise ; <strong>aucune fiche n’a été touchée</strong>. La modification passe par le
          circuit de proposition du manuel, qui la fera valider puis diffuser dans une version datée.
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {renvoi.map((r) => (
              <LienExterne key={r.code} href={`${MANUEL_URL}/?code=${encodeURIComponent(r.code)}`}
                className="bio-btn bio-btn--contour" style={{ ...styleBouton('contour', 'sm'), textDecoration: 'none' }} title={`Proposer sur ${r.code}`}>
                {`Proposer sur ${r.code}`}
              </LienExterne>
            ))}
            <Bouton taille="sm" onClick={() => setRenvoi([])}>Fermer</Bouton>
          </div>
        </div>
      )}

      {regle && d.peutParametrer && (
        <Parametrage d={d} onFait={recharger} onAnnule={() => setRegle(false)} />
      )}

      {conduit && selection.length > 0 && (
        <Conduire selection={selection} sources={sources}
          onFait={(r) => { setRenvoi(r); recharger(); }}
          onAnnule={() => setConduit(false)} />
      )}

      {compte.aRevoir === 0 ? (
        <EmptyState
          titre="Aucune fiche du catalogue ne publie d’intervalle de référence."
          indice="Ce n’est pas « rien à revoir par principe » : c’est que le champ « Valeurs de référence » est vide sur toutes les fiches publiées. Il se renseigne depuis le manuel, par le circuit de proposition." />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <FilterBar
                q={q} onQ={setQ} placeholder="Rechercher un examen, un code, une famille…"
                chips={[
                  { k: 'tous', label: 'Tous', n: compte.parametres },
                  { k: 'retard', label: 'Échéance dépassée', n: compte.enRetard },
                  { k: 'bientot', label: 'Bientôt dues', n: compte.bientot },
                  { k: 'jamais', label: 'Jamais revus', n: compte.jamaisRevues },
                  { k: 'amodifier', label: 'Conclus « à modifier »', n: compte.aModifier },
                  { k: 'sansintervalle', label: 'Sans intervalle publié', n: compte.sansIntervalle },
                ]}
                actif={filtre} onChip={setFiltre}
                total={compte.parametres} affiches={filtrees.length}
                onReset={() => { setQ(''); setFiltre('tous'); setFamille(''); }} />
            </div>
            <select value={famille} onChange={(e) => setFamille(e.target.value)}
              aria-label="Filtrer par famille d’analyses"
              style={{ ...inputStyle, width: 'auto', minWidth: 190, height: 32, fontSize: 12.5 }}>
              <option value="">Toutes les familles</option>
              {d.familles.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            {/* Le journal du PARAMÉTRAGE, à côté du bouton qui le modifie : ces
                    réglages s'écrivaient au journal sous une clé fixe sans aucun
                    accès à l'écran (audit traçabilité du 24/08/2026). */}
            <BoutonJournal reference="REVUE-REF" quoi="du paramétrage des revues" />
            {d.peutParametrer && !regle && (
              <Bouton taille="sm" onClick={() => setRegle(true)}>
                <I n="gear" s={13} />Rythme de revue
              </Bouton>
            )}
          </div>

          {d.peutRevoir && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-body)' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={toutCoche}
                  onChange={(e) => setCoches(e.target.checked
                    ? Object.fromEntries(selectionnables.map((l) => [l.code, true]))
                    : {})} />
                Tout sélectionner ({selectionnables.length})
              </label>
              <span style={{ color: 'var(--text-muted)' }}>{selection.length} sélectionné(s)</span>
              <span style={{ flex: 1 }} />
              <Bouton variante="primaire" onClick={() => setConduit(true)} disabled={!selection.length} title="Enregistrer une revue sur la sélection. Une trace par fiche, avec la photocopie de son intervalle.">
                <I n="check" s={14} />Conduire la revue
              </Bouton>
            </div>
          )}

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {pages.visibles.map((l, i) => (
              <div key={l.code} style={{ padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  {d.peutRevoir && (
                    <input type="checkbox" checked={!!coches[l.code]} disabled={l.sansIntervalle}
                      onChange={(e) => setCoches((c) => ({ ...c, [l.code]: e.target.checked }))}
                      aria-label={`Sélectionner ${l.code}`} />
                  )}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.code}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{l.libelle}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{l.famille}</span>
                  <span style={{ flex: 1 }} />
                  <Echeance l={l} sansParametrage={d.sansParametrage} />
                </div>

                {!l.sansIntervalle && (
                  <div style={{ marginTop: 6 }}><Intervalles lignes={l.intervalles} /></div>
                )}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 5, fontSize: 12.5, color: 'var(--text-muted)', alignItems: 'center' }}>
                  {l.derniere ? (
                    <span>
                      revu le {fmtDate(l.derniere.dateRevue)} par {l.derniere.revueParNom || '—'} —{' '}
                      <strong style={{ color: 'var(--text-body)' }}>
                        {l.derniere.conclusion === 'inchange' ? 'inchangé' : 'à modifier'}
                      </strong>
                      {' · '}{(l.derniere.instantane || []).length} intervalle(s) relu(s)
                      {/* Un intervalle modifié depuis la revue rend cette revue
                          caduque : elle atteste d'un état qui n'a plus cours. */}
                      {l.modifieDepuisRevue && (
                        <strong style={{ color: 'var(--amber-600)' }}> · modifié depuis</strong>
                      )}
                    </span>
                  ) : !l.sansIntervalle && (
                    <span title="Aucune revue n’a jamais été enregistrée sur ce paramètre. Sa périodicité court depuis la date d’origine déclarée au paramétrage."
                      style={{ color: 'var(--amber-600)', fontWeight: 700, cursor: 'help' }}>
                      jamais revu
                    </span>
                  )}
                  {l.periodiciteMois !== null && <span>tous les {l.periodiciteMois} mois</span>}
                  <span style={{ flex: 1 }} />
                  {l.historique.length > 0 && (
                    <Bouton taille="sm" onClick={() => setOuvert((o) => ({ ...o, [l.code]: !o[l.code] }))}>
                      {ouvert[l.code] ? 'Masquer' : `${l.historique.length} revue${l.historique.length > 1 ? 's' : ''}`}
                    </Bouton>
                  )}
                  <LienExterne href={`${MANUEL_URL}/?code=${encodeURIComponent(l.code)}`}
                    className="bio-btn bio-btn--contour" style={{ ...styleBouton('contour', 'sm'), textDecoration: 'none' }}
                    title="Ouvrir la fiche dans le manuel — c’est là, et seulement là, que l’intervalle se modifie.">
                    Ouvrir la fiche
                  </LienExterne>
                </div>

                {ouvert[l.code] && l.historique.length > 0 && (
                  <Historique lignes={l.historique} sources={sources} />
                )}
              </div>
            ))}
            {filtrees.length === 0 && (
              <div style={{ padding: 16 }}>
                <EmptyState titre="Aucun examen ne correspond à ces critères."
                  action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); setFamille(''); }}>Réinitialiser</Bouton>} />
              </div>
            )}
          </div>
          {/* 315 fiches sur trente-quatre écrans (relevé du 27/08/2026, P2). */}
          <Pagination page={pages.page} pages={pages.pages} debut={pages.debut} fin={pages.fin}
            total={pages.total} onPage={pages.setPage} quoi="examens" />

          {!d.peutRevoir && (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '84ch' }}>
              Vous consultez cet écran en lecture. Conduire une revue d’intervalle relève du responsable
              qualité ou d’un approbateur : décider qu’un intervalle publié reste valable est un acte
              biologique, pas un acte documentaire.
            </p>
          )}
        </>
      )}
      </>
      )}
    </div>
  );
}
